import axios from '@/api/axios';
import {toQueryString} from '@/api/queryString';
import type {
  AdminAfriexReward,
  AdminAfriexRewardCard,
  AdminPaginatedEnvelope,
  AfriexCurrencyCode,
  AfriexQueryParams,
  AfriexTransactionProcessor,
  AfriexTransactionType,
} from '@/types';

const commonEndpoint = '/v2/admin/rewards';
const rewardCardEndpoint = `${commonEndpoint}/cards`;

export type RewardResponse = AdminPaginatedEnvelope<AdminAfriexReward[]>;
export type RewardCardResponse = AdminPaginatedEnvelope<
  AdminAfriexRewardCard[]
>;
export type RewardFilters = AfriexQueryParams;

export type UpdateProcessorRequest = {
  reward: AfriexCurrencyCode;
  type: AfriexTransactionType;
  paymentMethodName: AfriexTransactionProcessor;
};

export const rewardService = {
  getList: async (filters: RewardFilters): Promise<RewardResponse> => {
    const params = {page: 0, limit: 20, ...filters};
    const url = `${commonEndpoint}?${toQueryString(params)}`;
    const response = await axios.get<RewardResponse>(url);
    return response.data;
  },
  getCardList: async (
    filters: RewardFilters,
    rewardId: string,
  ): Promise<RewardCardResponse | undefined> => {
    if (!rewardId) {
      return undefined;
    }
    const params = {page: 0, limit: 20, ...filters};
    const url = `${rewardCardEndpoint}/${rewardId}?${toQueryString(params)}`;
    const response = await axios.get<RewardCardResponse>(url);
    return response.data;
  },
  getItem: async (rewardId: string): Promise<AdminAfriexReward> => {
    const response = await axios.get<AdminAfriexReward>(
      commonEndpoint + `/${rewardId}`,
    );
    return response.data;
  },
  createItem: async (
    body: Partial<AdminAfriexReward>,
  ): Promise<AdminAfriexReward> => {
    const response = await axios.post<AdminAfriexReward>(commonEndpoint, body);
    return response.data;
  },
  createCardItems: async (
    codes: string[],
    rewardId: string,
  ): Promise<AdminAfriexReward> => {
    const response = await axios.post<AdminAfriexReward>(
      `${rewardCardEndpoint}/${rewardId}`,
      {
        codes,
      },
    );
    return response.data;
  },
  saveOne: async (
    itemId: string,
    body: Partial<AdminAfriexReward>,
  ): Promise<AdminAfriexReward> => {
    const response = await axios.patch<AdminAfriexReward>(
      commonEndpoint + `/${itemId}`,
      body,
    );
    return response.data;
  },
};
